import { InfoCircleOutlined } from '@ant-design/icons';
import { Form, Radio, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useContext, useEffect, useMemo } from 'react';

import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import type { Slot } from 'app/components/commons/Hotel/Types';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useGetHotel } from 'app/hooks/data/hotels/useHotel';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import { useHotelSpacesTypes } from 'app/hooks/data/useHotelSpacesTypes';
import { OpeningHoursType } from 'app/typings/core/admin/experiences/openingHours/openingHours';
import { ExperienceFieldType } from 'app/typings/experiences';
import { slugify } from 'app/utils/strings';

import { OpeningHoursDaily } from './OpeningHoursDaily';
import { OpeningHoursWeekly } from './OpeningHoursWeekly';
import './openingHours.scss';
import { presetOpeningHours } from './utils';

const RULES = [{ required: true, message: 'Required' }];

const RADIO_OPTIONS = [
  { label: 'Same every day', value: OpeningHoursType.WEEKLY },
  { label: 'Depends on the day', value: OpeningHoursType.DAILY },
];

type ISOSlot = [string, string];

type TimeSlot = Slot;

const normalizeSlot = (
  slot: ISOSlot | TimeSlot | Slot[] | unknown
): TimeSlot => {
  if (Array.isArray(slot) && slot.length === 2) {
    const fmt = (v: unknown): string | undefined => {
      if (typeof v === 'string') {
        return dayjs(v).format('HH:mm');
      }

      if (v && typeof (v as { format?: unknown }).format === 'function') {
        return (v as { format: (s: string) => string }).format('HH:mm');
      }

      return undefined;
    };

    return { start: fmt(slot[0]), end: fmt(slot[1]) };
  }

  if (slot && typeof slot === 'object' && 'start' in slot && 'end' in slot) {
    return slot as TimeSlot;
  }

  return { start: undefined, end: undefined };
};

export const OpeningHours = () => {
  const form = Form.useFormInstance();
  const { isViewMode } = useContext(ViewEditModeContext);

  const actualType = Form.useWatch(['openingHours', 'type'], form);
  const spaceIdsRaw = Form.useWatch(['spaceIds'], form);
  const hotelId = Form.useWatch(['hotelId'], form);
  const categoryId = Form.useWatch(['categoryId'], form);

  const { data: spaceTypes } = useHotelSpacesTypes();
  const { data: experienceCategories } = useExperiencesCategories();
  const { data: hotel } = useGetHotel(hotelId);

  const category = experienceCategories?.find((c) => c.id === categoryId);
  const spaceIds = useMemo(() => spaceIdsRaw ?? [], [spaceIdsRaw]);

  const presetHours = useMemo(
    () => presetOpeningHours(hotel?.spaces, spaceIds, spaceTypes),
    [hotel?.spaces, spaceIds, spaceTypes]
  );

  const presetTypes = useMemo(
    () => new Set(presetHours.map((p) => p.openingHours.type)),
    [presetHours]
  );

  useEffect(() => {
    if (!presetHours.length) {
      return;
    }

    const currentOH = form.getFieldValue(['openingHours']);

    if (
      !currentOH ||
      !Object.values(OpeningHoursType).includes(
        currentOH.type as OpeningHoursType
      )
    ) {
      return;
    }

    const matching = presetHours.find((preset) => {
      const oh = preset.openingHours;

      if (oh.type !== currentOH.type) {
        return false;
      }

      if (Boolean(oh.isAllDay) !== Boolean(currentOH.isAllDay)) {
        return false;
      }

      if (currentOH.isAllDay) {
        return true;
      }

      const presetSlots = oh.slots ?? [];
      const formSlots: TimeSlot[] = currentOH.slots ?? [];

      if (presetSlots.length !== formSlots.length) {
        return false;
      }

      return formSlots.every((formSlot, i) => {
        const normalized = normalizeSlot(presetSlots[i]);

        return (
          formSlot.start === normalized.start && formSlot.end === normalized.end
        );
      });
    });

    if (matching) {
      form.setFieldValue(['openingHours', 'type'], matching.name);
    }
  }, [presetHours, form]);

  const isPreset = (type: OpeningHoursType) =>
    actualType === type ||
    (actualType &&
      !Object.values(OpeningHoursType).includes(actualType) &&
      presetTypes.has(type));

  const isWeeklyPreset = isPreset(OpeningHoursType.WEEKLY);
  const isDailyPreset = isPreset(OpeningHoursType.DAILY);

  const displayedOpeningHours = useMemo(() => {
    if (actualType && !Object.values(OpeningHoursType).includes(actualType)) {
      const preset = presetHours.find((p) => p.name === actualType);

      if (preset?.openingHours) {
        const { isAllDay, slots } = preset.openingHours;

        return {
          type: actualType,
          isAllDay: !!isAllDay,
          slots: (slots || []).map(normalizeSlot),
        };
      }
    }

    return form.getFieldValue(['openingHours']);
  }, [actualType, presetHours, form]);

  useMemo(() => {
    if (
      displayedOpeningHours &&
      actualType &&
      !Object.values(OpeningHoursType).includes(actualType)
    ) {
      form.setFieldValue(['openingHours'], displayedOpeningHours);
    }
  }, [displayedOpeningHours, actualType, form]);

  return (
    <FormLineLayout
      label={
        <span className="opening-hours-label">
          Opening hours
          <Tooltip title="If added to a day package, this activity's opening hours will be limited to the day package's start and end times.">
            <InfoCircleOutlined />
          </Tooltip>
        </span>
      }
      isRequired
      labelOnTop
      disabled={isViewMode}
    >
      <div className="opening-hours-wrapper">
        <Form.Item
          name={['openingHours']}
          rules={RULES}
          className="opening-hours-form"
        >
          <Form.Item name={['openingHours', 'type']} noStyle>
            <Radio.Group disabled={isViewMode}>
              {presetHours.map((preset) => (
                <Radio key={slugify(preset.name)} value={preset.name}>
                  {`Same as ${preset.name}`}
                </Radio>
              ))}
              {RADIO_OPTIONS.map((option) => (
                <Radio key={option.value} value={option.value}>
                  {option.label}
                </Radio>
              ))}
              {category?.openingHours === ExperienceFieldType.OPTIONAL && (
                <Radio value="na">Not applicable</Radio>
              )}
            </Radio.Group>
          </Form.Item>
        </Form.Item>
        {isWeeklyPreset && <OpeningHoursWeekly />}
        {isDailyPreset && <OpeningHoursDaily />}
      </div>
    </FormLineLayout>
  );
};
